import { useEffect, useState } from 'react';

import { AUDIT_METRIC_COLORS } from '../../shared/constants/audit-metric-colors';
import * as styles from '../CommunityAuditAccuracyChart.css';

interface ChartData {
  accuracy: { pathData: string; points: Array<{ x: number; y: number; value: number }> };
  precision: { pathData: string; points: Array<{ x: number; y: number; value: number }> };
  recall: { pathData: string; points: Array<{ x: number; y: number; value: number }> };
  f1Score: { pathData: string; points: Array<{ x: number; y: number; value: number }> };
}

interface MetricsChartProps {
  chartData: ChartData;
  isLoading?: boolean;
}

/**
 * Renders an animated path for a metric
 */
function MetricPath({ pathData, color, animate, delay = 0 }: { pathData: string; color: string; animate: boolean; delay?: number }) {
  if (!pathData) return null;

  return (
    <path
      d={pathData}
      fill="none"
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      style={{
        strokeDasharray: animate ? 'none' : '1000',
        strokeDashoffset: animate ? '0' : '1000',
        transition: `stroke-dashoffset 1.5s cubic-bezier(0.4, 0, 0.2, 1) ${delay}s`,
      }}
    />
  );
}

/**
 * SVG chart component displaying multi-line metrics visualization
 * Shows accuracy, precision, recall, and F1 score paths with solid XDS colors.
 * Renders directly inside a BaseItem container provided by the parent.
 */
export function MetricsChart({ chartData, isLoading = false }: MetricsChartProps) {
  const [animate, setAnimate] = useState(false);

  // Trigger animation when chart data is available
  useEffect(() => {
    if (!isLoading && (chartData.accuracy.pathData || chartData.precision.pathData || chartData.recall.pathData || chartData.f1Score.pathData)) {
      // Small delay to ensure DOM is ready, then trigger animation
      const timer = setTimeout(() => setAnimate(true), 300); // Longer delay for chart

      return () => clearTimeout(timer);
    } else {
      setAnimate(false);
    }
  }, [isLoading, chartData]);

  return (
    <>
      {/* Chart area wrapper — constrains SVG intrinsic size via absolute positioning */}
      <div className={styles.chartWrapper}>
        {/* Y-axis labels */}
        <div className={styles.yAxisLabels}>
          <span className={styles.yAxisLabel}>100%</span>
          <span className={styles.yAxisLabel}>0%</span>
        </div>
        <svg className={styles.sparklineSvg} preserveAspectRatio="none" viewBox="0 0 300 200">
          {/* Background grid lines for visual reference */}
          <defs>
            <pattern height="12" id="grid" patternUnits="userSpaceOnUse" width="30">
              <path d="M 30 0 L 0 0 0 12" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />
            </pattern>
          </defs>

          <rect fill="url(#grid)" height="100%" width="100%" />

          {/* Multi-line paths for each metric with animation using solid XDS colors */}
          <MetricPath animate={animate} color={AUDIT_METRIC_COLORS.accuracy} delay={0} pathData={chartData.accuracy.pathData} />
          <MetricPath animate={animate} color={AUDIT_METRIC_COLORS.precision} delay={0.2} pathData={chartData.precision.pathData} />
          <MetricPath animate={animate} color={AUDIT_METRIC_COLORS.recall} delay={0.4} pathData={chartData.recall.pathData} />
          <MetricPath animate={animate} color={AUDIT_METRIC_COLORS.f1Score} delay={0.6} pathData={chartData.f1Score.pathData} />
        </svg>
      </div>
    </>
  );
}
